/**
 * DIAG-DISCOUNT ("Diagnostic waiver") — the single PO-authored copy source.
 * Transcribed VERBATIM from
 * `arogyakhata-orchestrator/state/epics/DIAG-DISCOUNT/plan/copy-attill.md`
 * — the at-till strings (§1-12, 45 strings, ratified 2026-09-05) PLUS the v5
 * "record = intent · apply at settlement" strings (§13, 59 strings, added
 * 2026-09-07 — §13.8's empty-state group is not yet landed here, see
 * report) PLUS §13.10 (12 keys) + §13.11 (5 keys), added 2026-09-08 (FE-G) —
 * the recorded-waiver bill/Diagnostics-tab display and the one-per-admission
 * warning. ONE file — never a second copy source. Frontend AND
 * backend-facing copy wire to the KEY below, never to a literal — a later
 * reword is one edit here.
 *
 * §14 of the source doc is the migration map: several at-till (§1-12) keys
 * are marked RETIRE or MOVE in comments above their entries below. Per §14,
 * retiring/moving keys are LEFT IN PLACE until R5 (FE-C) deletes the
 * settle-card waiver input in the same change — do not remove them early.
 *
 * OWNER DECISION, 2026-09-07 — ship v5 WITHOUT the reverse screen
 * (`bk-1058` stays open). The reverse endpoint + API client are built but no
 * screen calls them, so the old copy that told an admin to "reverse it" was
 * instructing them to use a control the product does not offer. This pass
 * reworded every string that promised a reverse action and RETIRED the
 * reverse-only keys (§13.3's 13 keys, its 3 server refusals, and
 * `record.alreadyActive.action` — 17 total). Retired keys are marked
 * "RETIRED — do not wire" in place, kept verbatim (not deleted) so whoever
 * picks up `bk-1058` starts from finished copy. `diagWaiver.guidance.beforeCommit`
 * is on its THIRD value here — the 2026-09-05 rewrite named a real correction
 * path (reverse-while-unapplied) that turned out to have no screen behind it;
 * this value is the honest one. Two of the retired reverse strings
 * (`reverse.disabled.fullyApplied`, and the refusal it stands in for) are
 * separately KNOWN WRONG (`bk-1056`/`bk-1060` — false for a lapsed waiver,
 * references a receipt that was never issued) and must be fixed in the same
 * change that ships the reverse UI, not here.
 *
 * Developers must not write, shorten, expand or "improve" any of these
 * strings. If a string is needed that is not here, stop and ask the PO — do
 * not invent one.
 *
 * Placeholders are `{curly}` tokens filled by the caller via `formatDiagWaiverCopy`.
 */

export const DIAG_WAIVER_COPY = {
  // 1. Settle payment card — the waiver section
  // v5 §14 disposition — settle no longer takes waiver input:
  //  - section.title / section.helper: RETIRE (no waiver section on settle).
  //  - field.amount.* / field.reason.*: MOVE — superseded by
  //    `diagWaiver.record.*` (§13.1) on the record dialog.
  // Left in place per §14 ("do not delete until R5 lands" — FE-C removes
  // the settle-card input and these together).
  'diagWaiver.section.title': 'Diagnostic waiver',
  'diagWaiver.section.helper': 'A diagnostic waiver reduces what this patient pays for these tests. Enter it here, at the time you collect.',
  'diagWaiver.field.amount.label': 'Diagnostic waiver (₹)',
  'diagWaiver.field.amount.placeholder': '0',
  'diagWaiver.field.amount.helper': 'Leave blank if there is no waiver on this test.',
  'diagWaiver.field.reason.label': 'Reason for the waiver',
  'diagWaiver.field.reason.requiredMark': '*',
  'diagWaiver.field.reason.placeholder': "e.g. Approved by admin for the patient's family",
  'diagWaiver.field.reason.helper': 'Required once you enter any waiver. Write why it was allowed and who approved it — it is saved with the receipt under your name.',

  // 2. Live totals as the admin types
  'diagWaiver.totals.billed.label': 'Billed',
  'diagWaiver.totals.billed.helper': 'Full price of the tests you have selected.',
  'diagWaiver.totals.waiver.label': 'Diagnostic waiver',
  'diagWaiver.totals.toCollect.label': 'To collect',
  'diagWaiver.totals.empty': '—',
  'diagWaiver.totals.line': 'Billed {billed} · Diagnostic waiver {waiver} · To collect {toCollect}',

  // 3. Submitting
  'diagWaiver.submit.default': 'Collect {toCollect}',
  'diagWaiver.submit.zero': 'Record ₹0 settlement',
  'diagWaiver.submit.zero.helper': 'Nothing is being collected for these tests. A receipt is still made, so the tests are closed and the record is complete.',
  'diagWaiver.submit.busy': 'Saving…',
  'diagWaiver.submit.disabled.noSelection': 'Select at least one test to collect.',

  // 4. Guidance for a mistyped amount (owner-accepted: void and collect again)
  // REWORDED AGAIN 2026-09-07 (§14) — THIRD value. The 2026-09-05 rewrite
  // said "a waiver can be reversed while it is still unapplied": true of the
  // endpoint, false of the product, because no screen calls it (`bk-1058`).
  // This is the honest version. Key unchanged.
  'diagWaiver.guidance.beforeCommit': 'Check the amount before you record it. A recorded waiver cannot be changed or removed. Once the diagnostics have been settled against it, correcting an amount means voiding that receipt and collecting again.',
  // §14 / `bk-1041` note (never landed in this file — `bk-1059` gap,
  // backfilled here): these were written for a point of no return and have
  // never been wired. Under v5 the RECORD action is that moment, and §13.1
  // gives it dedicated `record.confirm.*` strings — wire those. This
  // generic `confirm.*` set can then be retired or kept for the settle
  // step; PO's call at the next pass, not this task's.
  'diagWaiver.confirm.title': 'Confirm this collection',
  'diagWaiver.confirm.body': 'Billed {billed} · Diagnostic waiver {waiver} · To collect {toCollect}.<br>A waiver cannot be changed afterwards — to correct one, void this receipt and collect again.',
  'diagWaiver.confirm.action': 'Confirm and collect',
  'diagWaiver.confirm.cancel': 'Go back',

  // 5. Success confirmation
  'diagWaiver.success.title': 'Payment recorded',
  'diagWaiver.success.body': 'Collected {toCollect} · Diagnostic waiver {waiver} · Billed {billed}',
  'diagWaiver.success.bodyNoWaiver': 'Collected {toCollect}',
  'diagWaiver.success.zero': '₹0 collected · Diagnostic waiver {waiver} · Billed {billed}',
  'diagWaiver.success.receipt': 'Receipt {receiptNumber}',
  'diagWaiver.success.done': 'Done',

  // 6. Errors the admin sees while typing
  // v5 §14: MOVE to the record dialog. The cap wording differs there (the
  // stay's outstanding, not one test's amount) — use
  // `diagWaiver.record.server.exceedsOutstanding` (§13.5) for that case;
  // `.reasonMissing` / `.amountInvalid` carry over unchanged in meaning.
  'diagWaiver.error.overAmount': "The diagnostic waiver cannot be more than this test's {testAmount}. Enter {testAmount} or less.",
  'diagWaiver.error.reasonMissing': 'Add a reason for the diagnostic waiver before you collect.',
  'diagWaiver.error.amountInvalid': 'Enter the waiver as a rupee amount, like 250.',
  'diagWaiver.error.submitFailed': 'The payment was not recorded. Your amounts and reason are still here — check them and try again.',

  // 7. Disabled states — every one says why
  // v5 §14: RETIRE — there is no per-row input on the settle card to
  // disable any more. Their job passes to `diagWaiver.record.disabled.*`
  // (§13.6) on the record entry button.
  'diagWaiver.disabled.walkIn': 'Diagnostic waivers can only be given on an admitted patient’s tests. This test belongs to a walk-in visit.',
  'diagWaiver.disabled.walkIn.tooltip': 'Admitted patients only — this is a walk-in test.',
  'diagWaiver.disabled.nonAdmin': 'Diagnostic waivers can only be entered by an admin. You can still collect this payment as usual.',
  'diagWaiver.disabled.nonAdmin.tooltip': 'Admin only — you can still collect.',
  'diagWaiver.note.mixedBatch': 'Only the admitted patient’s tests can carry a diagnostic waiver. The walk-in tests in this list will be collected in full.',

  // 8. Receipt
  'receipt.line.diagnosticWaiver': 'Diagnostic waiver',
  'receipt.line.waiver': 'Waiver',

  // 9. Diagnostics Bill (the stay-level PDF)
  'diagBill.footer.waiver': 'Less: Diagnostic waiver',

  // 10. Lab report
  // Owner-directed change (2026-09-07): this tile sits on the day-wide
  // Daily Collection Summary strip alongside Cash/UPI/Card/Refunds/Net
  // Collection, which mixes OPD and IPD activity — "Diagnostic waiver"
  // alone is ambiguous there. Value only, key unchanged (FE-4 imports by
  // name). Every admission-scoped surface (settle card, the batch dialog,
  // the Diagnostics Bill footer, the receipt line) keeps "Diagnostic
  // waiver" — the IPD context is already obvious on those screens.
  // NOTE for copy-attill.md: the doc still shows the pre-2026-09-07 value;
  // flagged to the orchestrator to annotate the source doc (see report).
  'labReport.diagnosticWaiverGiven.label': 'IPD Diagnostic waiver',
  'labReport.diagnosticWaiverGiven.helper': 'Total waived on diagnostics in this period. Already taken off the collected figure — do not subtract it again.',
  'labReport.diagnosticWaiverGiven.empty': '₹0',

  // 11. Server refusals that reach a user
  // v5 §14: `.notPermitted` / `.walkInNotAllowed` / `.reasonRequired` MOVE
  // to the record endpoint — see `diagWaiver.record.server.*` (§13.5); they
  // no longer fire at settlement (settle takes no waiver input under v5).
  // `.exceedsAmount` KEEPS firing at settlement, unchanged, as the server's
  // own belt-and-braces cap-before-mint check.
  'diagWaiver.server.notPermitted': 'You do not have permission to enter a diagnostic waiver. An admin can do this. Your collection has not been recorded.',
  'diagWaiver.server.walkInNotAllowed': 'A diagnostic waiver can only be given on an admitted patient’s tests. Remove the waiver from the walk-in tests and collect again.',
  'diagWaiver.server.exceedsAmount': 'The diagnostic waiver is more than this test’s amount. The most that can be waived on it is {maxAmount}.',
  'diagWaiver.server.reasonRequired': 'A reason is required for a diagnostic waiver. Add one and collect again.',

  // 12. Unchanged labels listed here so nobody "tidies" them
  'admissionWaiver.button.label': 'Waiver / concession',

  // ===========================================================================
  // §13 — v5 STRINGS (record · reverse · the settle card's read-only line)
  // New placeholders: {outstanding} {recorded} {applyingNow} {unused} {date} {name}
  // ===========================================================================

  // 13.1 Record dialog — opened by an admin from the admission screen
  'diagWaiver.record.title': 'Record a diagnostic waiver',
  'diagWaiver.record.intro': 'This records a waiver against this stay’s diagnostics. **No money is collected now and nothing is deducted yet** — it is applied automatically when the diagnostics are settled at the counter.',
  'diagWaiver.record.outstanding.label': 'Diagnostics outstanding',
  'diagWaiver.record.outstanding.hint': 'The waiver is not taken off this figure. Both numbers stay on screen until the diagnostics are settled.',
  'diagWaiver.record.amount.label': 'Diagnostic waiver (₹)',
  'diagWaiver.record.amount.helper': 'This is the most that will be taken off when the diagnostics are settled.',
  'diagWaiver.record.reason.label': 'Reason for the waiver',
  'diagWaiver.record.reason.helper': 'Required. Write why it was allowed and who approved it — it is saved on the stay under your name and stays on the record.',
  // REWORDED 2026-09-07 — the old value promised a reverse action that has
  // no screen in this release. This is the honest version: there is no
  // correction path until it is settled.
  'diagWaiver.record.correction.note': 'Check the amount before you record it. A recorded waiver cannot be changed or removed. Once the diagnostics have been settled against it, an amount can be corrected by voiding that receipt and collecting again.',
  'diagWaiver.record.submit': 'Record waiver',
  'diagWaiver.record.submit.busy': 'Recording…',
  'diagWaiver.record.confirm.title': 'Confirm this diagnostic waiver',
  'diagWaiver.record.confirm.body': 'Record a diagnostic waiver of {recorded} on this stay? **No money is collected now.** The receptionist applies it when they settle the diagnostics.',
  'diagWaiver.record.confirm.action': 'Record waiver',
  'diagWaiver.record.confirm.cancel': 'Go back',
  'diagWaiver.record.success.title': 'Waiver recorded',
  'diagWaiver.record.success.body': '{recorded} recorded on this stay. It applies when the diagnostics are settled — nothing has been collected yet.',
  'diagWaiver.record.success.done': 'Done',

  // 13.2 One waiver already recorded (the one-active rule) — REWRITTEN
  // 2026-09-07. The old `.body` said "reverse it first and record a new
  // one" — no screen can do that. The admin is told what is true, never
  // instructed to take an action the product does not offer.
  'diagWaiver.record.alreadyActive.title': 'A waiver is already recorded on this stay',
  'diagWaiver.record.alreadyActive.body': '{recorded} was recorded on {date} by {name}. It will be applied when the receptionist settles this stay’s diagnostics. Only one waiver can be active at a time, so another cannot be recorded until this one has been settled.',
  // NEW 2026-09-07 — the honest correction path, and the only one. Reads as
  // a rule, not as a fault the admin has to fix.
  'diagWaiver.record.alreadyActive.correction': 'A recorded waiver cannot be changed or removed. Once the diagnostics have been settled against it, an amount can be corrected by voiding that receipt and collecting again.',
  // RETIRED 2026-09-07 — do not wire. It was a button into a screen that
  // does not exist. Kept, struck in spirit, for `bk-1058`.
  'diagWaiver.record.alreadyActive.action': 'Reverse the recorded waiver',
  'diagWaiver.record.alreadyActive.button': 'Waiver recorded — {recorded}',

  // 13.3 Reverse — RETIRED FOR THIS RELEASE (owner decision, 2026-09-07).
  // The reverse endpoint and its API client are built, but nothing calls
  // them (`bk-1058` stays open). Every key below is RETIRED — do not wire
  // any of them. Kept verbatim, not deleted, so whoever picks up `bk-1058`
  // starts from finished copy.
  // ⚠ `reverse.disabled.fullyApplied` is separately KNOWN WRONG
  // (`bk-1056`/`bk-1060`): the backend reaches this refusal for a waiver
  // that merely lapsed at discharge, where nothing was collected and no
  // receipt exists. Harmless while no screen calls it — must be fixed in
  // the same change that ships the reverse UI, not here.
  'diagWaiver.reverse.action': 'Reverse this waiver',
  'diagWaiver.reverse.title': 'Reverse the recorded waiver',
  'diagWaiver.reverse.body': '{unused} of this waiver has not been applied yet. Reversing puts it back on what the patient owes for diagnostics. The record stays on file with your reason.',
  'diagWaiver.reverse.amount.label': 'Amount to reverse (₹)',
  'diagWaiver.reverse.amount.helper': 'You can reverse all of it, or part.',
  'diagWaiver.reverse.reason.label': 'Reason for reversing',
  'diagWaiver.reverse.confirm.action': 'Reverse waiver',
  'diagWaiver.reverse.busy': 'Reversing…',
  'diagWaiver.reverse.success': 'Waiver reversed. {unused} is back on this stay’s diagnostics.',
  'diagWaiver.reverse.reversedStamp': 'Reversed on {date} by {name}',
  'diagWaiver.reverse.disabled.fullyApplied': 'This waiver has already been applied in full. Money that has been collected cannot be un-waived — void the receipt and collect again instead.',
  'diagWaiver.reverse.disabled.stayClosed': 'This stay is closed, so the waiver can no longer be reversed.',
  'diagWaiver.reverse.partialNote': '{applyingNow} has already been applied and cannot be reversed.',

  // 13.4 Settle payment card — the receptionist's READ-ONLY line
  'diagWaiver.settle.headline': 'Outstanding {outstanding} · Waiver recorded {recorded}',
  'diagWaiver.settle.recorded.label': 'Waiver recorded',
  'diagWaiver.settle.applyingNow.label': 'Applying to this settlement',
  'diagWaiver.settle.unused.label': 'Still unused',
  'diagWaiver.settle.readOnly.note': 'Recorded by an admin. It is applied automatically — there is nothing for you to enter.',
  'diagWaiver.settle.unused.note': '{unused} is left over and stays available for the next settlement on this stay.',
  'diagWaiver.settle.none': '', // *(render nothing)* — no recorded waiver ⇒ no line at all, not a ₹0 row.
  'diagWaiver.settle.zeroNet.submit': 'Record ₹0 settlement',
  'diagWaiver.settle.zeroNet.helper': 'The waiver covers these tests in full. Nothing is collected, but a receipt is still made so the tests are closed.',

  // 13.5 Record-endpoint refusals a user will actually see
  'diagWaiver.record.server.notPermitted': 'You do not have permission to record a diagnostic waiver. An admin can do this. Nothing has been recorded.',
  'diagWaiver.record.server.walkInNotAllowed': 'A diagnostic waiver can only be recorded for an admitted patient.',
  'diagWaiver.record.server.exceedsOutstanding': 'That is more than the {maxAmount} still outstanding on this stay’s diagnostics. Enter {maxAmount} or less.',
  'diagWaiver.record.server.reasonRequired': 'A reason is required. Add one and record again.',
  // REWORDED 2026-09-07 — the old value said "Reverse it before recording
  // another", an instruction with no screen behind it.
  'diagWaiver.record.server.alreadyActive': 'A waiver of {recorded} is already recorded on this stay. It will be applied when the diagnostics are settled; another cannot be recorded until then.',
  'diagWaiver.record.server.stayClosed': 'This stay is no longer open, so a waiver cannot be recorded. Adjustments now happen at final settlement.',
  'diagWaiver.record.server.unavailable': 'The waiver could not be recorded just now. Nothing has been saved — try again in a moment.',
  // RETIRED with §13.3, 2026-09-07 — no screen calls the reverse endpoint
  // in this release. Kept verbatim for `bk-1058`.
  'diagWaiver.reverse.server.noActive': 'There is no recorded waiver on this stay to reverse.',
  // RETIRED with §13.3, and separately KNOWN WRONG — see the §13.3 note
  // above (`bk-1056`/`bk-1060`).
  'diagWaiver.reverse.server.fullyApplied': 'This waiver has already been applied in full and cannot be reversed.',
  'diagWaiver.reverse.server.exceedsRemaining': 'Only {unused} has not been applied yet. Enter {unused} or less.',
  'diagWaiver.settle.server.staleScreen': 'This screen is out of date. Refresh the page and settle again — the recorded waiver is applied automatically.',

  // 13.6 Record entry-button disabled states — each says why
  'diagWaiver.record.disabled.nonAdmin': 'Diagnostic waivers can only be recorded by an admin.',
  'diagWaiver.record.disabled.walkIn': 'Diagnostic waivers can only be recorded for an admitted patient.',
  'diagWaiver.record.disabled.stayClosed': 'This stay is closed. A diagnostic waiver can no longer be recorded.',
  'diagWaiver.record.disabled.nothingOutstanding': 'There is nothing outstanding on this stay’s diagnostics to waive.',

  // 13.9 The recorded amount is FIXED — it does not follow the bill.
  // ADDED 2026-09-07 — this key was in the source doc's §13.9 but had never
  // landed in this file (a gap alongside `bk-1059`, transcribed verbatim
  // now). REWORDED value per the same pass: the old wording (never shipped
  // here) ended "…and you can reverse it" — a promise with no screen
  // behind it. This is the honest version.
  'diagWaiver.record.fixedAmount.note': 'The amount you record is fixed. If more tests are added later it does not grow, and if tests are cancelled it does not shrink — anything left over simply goes unused.',

  // 13.7 The two controls, now side by side on ONE panel
  'diagWaiver.record.button.label': 'Diagnostic waiver',
  'admissionWaiver.button.tooltip': 'Reduces room, procedure and item charges on this admission’s bill.',
  'diagWaiver.record.button.tooltip': 'Records an amount to come off this stay’s diagnostic tests when they are settled. No money moves now.',

  // 13.10 The recorded waiver, shown on the bill and the Diagnostics tab
  // [2026-09-08] — FE-G. The owner recorded a waiver and asked "how many is
  // recorded?" — the figures were on both bills already; no screen read
  // them. `.pending` is the default state, and the one the owner was missing.
  'diagWaiver.bill.recorded.title': 'Diagnostic waiver',
  'diagWaiver.bill.recorded.amount.label': 'Recorded',
  'diagWaiver.bill.recorded.pending': 'Applies when this stay’s diagnostics are settled. **No money has moved yet.**',
  'diagWaiver.bill.recorded.partly': '{applied} applied so far · {unused} still to apply',
  'diagWaiver.bill.recorded.fullyApplied': 'Fully applied at settlement.',
  'diagWaiver.bill.recorded.unusedNote': '{unused} was not needed and stays unused.',
  'diagWaiver.bill.recorded.attribution': 'Recorded by {name} on {date}',
  'diagWaiver.bill.recorded.reason': 'Reason: {reason}',
  'diagWaiver.bill.recorded.reversed': 'Reversed on {date} by {name}.',
  'diagWaiver.bill.recorded.none': '', // *(render nothing)* — no waiver recorded ⇒ no line at all, not a ₹0 row.
  'diagWaiver.diagnostics.recorded.label': 'Diagnostic waiver recorded',
  'diagWaiver.diagnostics.recorded.hint': 'Comes off when these tests are settled. **The amounts listed above are before the waiver.**',

  // 13.11 One waiver per admission — the warning [2026-09-08] — FE-G, `bk-1064`.
  // Owner-warned limitation, NOT a deliberate policy: once a recorded waiver
  // has been settled the stay is permanently blocked (the "one active
  // waiver" marker is never cleared on spend, only on reverse). Shown BEFORE
  // the admin commits an amount, not only in the refusal met days later.
  'diagWaiver.record.oncePerStay.notice': '**One diagnostic waiver per admission.** Once this waiver has been settled, no further waiver can be recorded for this stay — so choose an amount that covers the whole admission, including tests that may still be ordered.',
  'diagWaiver.record.oncePerStay.confirm': 'This is the only diagnostic waiver that can be recorded for this stay.',
  'diagWaiver.record.disabled.spent': 'Waiver already settled — one per admission',
  'diagWaiver.record.disabled.spent.tooltip': 'This stay’s diagnostic waiver has already been settled, and only one can be recorded per admission.',
  // NEW refusal — the dead-end case. Server-side, when a waiver is active but
  // FULLY APPLIED (`remaining_paise: 0`) — split from `record.server.alreadyActive`
  // (§13.5), whose "…another cannot be recorded until then" is only true
  // while some of the recorded waiver is still unapplied.
  'diagWaiver.record.server.spentNoFurther': 'This stay’s diagnostic waiver has already been settled. Only one diagnostic waiver can be recorded per admission, so no further waiver can be recorded for this stay.',
};

/** Fills `{token}` placeholders in a copy-source string with caller-supplied values. */
export function formatDiagWaiverCopy(key, vars = {}) {
  const template = DIAG_WAIVER_COPY[key] || '';
  return template.replace(/\{(\w+)\}/g, (match, token) => (
    Object.prototype.hasOwnProperty.call(vars, token) ? vars[token] : match
  ));
}

export default DIAG_WAIVER_COPY;
